import { AdminDateRangePicker, AdminPage, App, DataTable, DetailDescriptions, DetailDrawer, SearchPanel, StatusTag, Toolbar } from '@wanjianshi/admin-ui';
import type { ApiResponse, PageResult } from '@wanjianshi/shared-contracts';
import { ApiError } from '@wanjianshi/shared-http';
import { formatDateTime } from '@wanjianshi/shared-utils';
import { Alert, Form, Image, Input, Select, Tabs, Typography } from '@wanjianshi/admin-ui';
import type { TableColumnsType } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, isAdminUnauthorizedError } from '@/services/api/client';
import { UserAvatar } from '@/components/UserAvatar';
import { ResourcePageExtra } from '@/features/resources/ResourcePageExtra';
import { ResourceRowActions } from '@/features/resources/ResourceRowActions';
import { RESOURCE_DEFINITIONS, endpointBase, labelForField } from '@/features/resources/definitions';
import {
  PlatformUserFoundedTeamCreditConsumptions,
  PlatformUserPersonalCreditConsumptions,
} from '@/features/users/PlatformUserCreditPanels';
import { formatAdminRoleNames } from '@/utils/adminRole';

type Row = Record<string, unknown> & { id?: string | number };

interface RechargeSearchValues {
  merchant_order_id?: string;
  transaction_id?: string;
  user_nickname?: string;
  user_mobile?: string;
  payment_status?: string;
  payment_time?: [Dayjs, Dayjs] | null;
}

const DEFAULT_FIELDS = ['id', 'name', 'nickname', 'title', 'status', 'create_time', 'update_time'];

function positiveInt(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

const membershipStatusLabels: Record<string, string> = {
  active: '有效',
  inactive: '未开通',
  expired: '已过期',
  paused: '已暂停',
  cancelled: '已取消',
  canceled: '已取消',
  pending: '待生效',
  completed: '已完成',
  failed: '失败',
};

function PlatformUserAvatar({ src, name }: { src?: string; name: string }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return <UserAvatar alt={name} size={40} />;
  }
  return (
    <Image
      className="platform-user-avatar-image"
      width={40}
      height={40}
      src={src}
      preview={{ mask: '预览' }}
      onError={() => setFailed(true)}
    />
  );
}

function renderValue(value: unknown, key: string, base: string, row?: Row) {
  if (base === '/admin/users' && key === 'avatar') {
    const avatar = String(value || '').trim() || undefined;
    const name = String(row?.nickname || row?.username || '用户').trim();
    return <PlatformUserAvatar src={avatar} name={name} />;
  }
  if ((base === '/admin/users' || base === '/admin/admin-users') && key === 'status') {
    const normal = Number(value) === 0;
    return <StatusTag value={value as number} label={normal ? '正常' : '停用'} color={normal ? 'success' : 'default'} />;
  }
  if (base === '/admin/users' && key === 'account_points') {
    return (
      <Typography.Text strong>
        {new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 6 }).format(Number(value || 0))}
      </Typography.Text>
    );
  }
  if (base === '/admin/users' && key === 'inviter') {
    if (!value || typeof value !== 'object') {
      return <Typography.Text type="secondary">未绑定</Typography.Text>;
    }
    const inviter = value as Record<string, unknown>;
    const name = String(inviter.nickname || inviter.username || `用户 ${String(inviter.id)}`);
    const mobile = String(inviter.mobile_masked || '').trim();
    return (
      <span>
        {name}
        <Typography.Text type="secondary">
          {` · ID ${String(inviter.id)}${mobile ? ` · ${mobile}` : ''}`}
        </Typography.Text>
      </span>
    );
  }
  if (base === '/admin/recharge' && [
    'credited_points',
    'reversed_points',
    'remaining_reversible_points',
    'account_balance',
    'frozen_balance',
    'available_points',
    'max_reversible_points',
  ].includes(key)) {
    return new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 6 }).format(Number(value || 0));
  }
  if (base === '/admin/recharge' && key === 'reversal_status') {
    const label = String(value || '不可冲正');
    const color = label === '未冲正' ? 'blue' : label === '部分冲正' ? 'orange' : label === '已全部冲正' ? 'green' : 'default';
    return <StatusTag value={label} label={label} color={color} />;
  }
  if (base === '/admin/recharge' && key === 'payment_method') {
    const channel = String(value || '').trim();
    const channelLabels: Record<string, string> = {
      bank_transfer: '公对公转账',
      alipay: '支付宝',
      wxpay: '微信支付',
      wechat: '微信支付',
    };
    return (channelLabels[channel.toLowerCase()] ?? channel) || '-';
  }
  if (base === '/admin/recharge' && ['merchant_order_id', 'transaction_id'].includes(key)) {
    const orderNumber = String(value || '').trim();
    return orderNumber
      ? <Typography.Text copyable={{ text: orderNumber }}>{orderNumber}</Typography.Text>
      : '-';
  }
  if (key === 'role_codes' && Array.isArray(value)) {
    return formatAdminRoleNames(value.map(String)).join('、') || '-';
  }
  const membershipResource = [
    '/admin/personal-memberships',
    '/admin/team-memberships',
    '/admin/membership-plans',
    '/admin/teams',
  ].includes(base);
  const teamDetailResource = base.startsWith('/admin/teams/');
  if ((membershipResource || teamDetailResource) && (key === 'status' || key === 'subscription_status' || key === 'team_status')) {
    if (base === '/admin/membership-plans' && key === 'status') {
      const enabled = Number(value) === 1;
      return <StatusTag value={value as number} label={enabled ? '启用' : '停用'} color={enabled ? 'success' : 'default'} />;
    }
    if ((base === '/admin/teams' && key === 'status') || key === 'team_status' || (teamDetailResource && key === 'status' && typeof value === 'number')) {
      const normal = Number(value) === 0;
      return <StatusTag value={value as number} label={normal ? '正常' : '停用'} color={normal ? 'success' : 'default'} />;
    }
    const normalized = String(value ?? '').toLowerCase();
    return <StatusTag value={normalized} label={membershipStatusLabels[normalized] ?? '未知状态'} />;
  }
  if ((membershipResource || teamDetailResource) && key === 'billing_cycle') {
    return ({ monthly: '月度', quarterly: '季度', yearly: '年度' } as Record<string, string>)[String(value)] ?? '-';
  }
  if ((membershipResource || teamDetailResource) && key === 'membership_scope') {
    return String(value) === 'team' ? '团队会员' : '个人会员';
  }
  if (key === 'status' || key === 'subscription_status' || key === 'severity') return <StatusTag value={value as string | number | null} />;
  if (key.endsWith('_time') || key.endsWith('_at')) return formatDateTime(value as string | number | null);
  if (key === 'progress') return `${String(value ?? 0)}%`;
  if (typeof value === 'boolean') return value ? '是' : '否';
  if (Array.isArray(value)) return value.join('、') || '-';
  if (value && typeof value === 'object') return <Typography.Text code>{JSON.stringify(value)}</Typography.Text>;
  return String(value ?? '-');
}

function detailPath(endpoint: string, row: Row): string | null {
  const base = endpointBase(endpoint);
  if (base === '/admin/users') return `/admin/users/${row.id}`;
  if (base === '/admin/roles') return `/admin/roles/${row.id}`;
  if (base === '/admin/generation-tasks') return `/admin/generation-tasks/${row.task_id}`;
  if (base === '/admin/teams') return `/admin/teams/${row.id}`;
  if (base === '/admin/personal-memberships') return `/admin/personal-memberships/${row.subject_id}`;
  if (base === '/admin/team-memberships') return `/admin/team-memberships/${row.subject_id}`;
  if (base === '/admin/city-agents') return `/admin/city-agents/${row.id}`;
  if (base === '/admin/templates') return `/admin/templates/${row.id}`;
  if (base === '/admin/creator-certifications/users') return `/admin/creator-certifications/users/${row.user_id}`;
  return null;
}

export function ResourceListPage({
  title,
  description,
  endpoint,
  fields = DEFAULT_FIELDS,
}: {
  title: string;
  description?: string;
  endpoint: string;
  fields?: string[];
}) {
  const { message } = App.useApp();
  const base = endpointBase(endpoint);
  const isPlatformUserList = base === '/admin/users';
  const isAdminUserList = base === '/admin/admin-users';
  const isUserAccountList = isPlatformUserList || isAdminUserList;
  const isTeamMembershipList = base === '/admin/team-memberships';
  const isProjectList = base === '/admin/projects';
  const isRechargeList = base === '/admin/recharge';
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = positiveInt(searchParams.get('page'), 1);
  const pageSize = Math.min(200, positiveInt(searchParams.get('page_size'), 20));
  const keyword = searchParams.get('keyword')?.trim() ?? '';
  const teamName = searchParams.get('team_name')?.trim() ?? '';
  const projectName = searchParams.get('project_name')?.trim() ?? '';
  const userNickname = searchParams.get('user_nickname')?.trim() ?? '';
  const userMobile = searchParams.get('user_mobile')?.trim() ?? '';
  const merchantOrderId = searchParams.get('merchant_order_id')?.trim() ?? '';
  const transactionId = searchParams.get('transaction_id')?.trim() ?? '';
  const paymentStatus = searchParams.get('payment_status') ?? '10';
  const paymentStartDate = searchParams.get('start_date')?.trim() ?? '';
  const paymentEndDate = searchParams.get('end_date')?.trim() ?? '';
  const [userSearchForm] = Form.useForm();
  const [teamSearchForm] = Form.useForm();
  const [projectSearchForm] = Form.useForm();
  const [rechargeSearchForm] = Form.useForm<RechargeSearchValues>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Row | null>(null);
  const listAbortRef = useRef<AbortController | null>(null);
  const detailAbortRef = useRef<AbortController | null>(null);
  const definition = RESOURCE_DEFINITIONS[base];
  const load = useCallback(async () => {
    listAbortRef.current?.abort();
    const controller = new AbortController();
    listAbortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.get<ApiResponse<PageResult<Row>>>(endpoint, {
        params: isRechargeList
          ? {
              page,
              page_size: pageSize,
              merchant_order_id: merchantOrderId || undefined,
              transaction_id: transactionId || undefined,
              user_nickname: userNickname || undefined,
              user_mobile: userMobile || undefined,
              payment_status: paymentStatus === 'all' ? undefined : Number(paymentStatus),
              start_date: paymentStartDate || undefined,
              end_date: paymentEndDate || undefined,
            }
          : isUserAccountList
          ? {
              page,
              page_size: pageSize,
              user_nickname: userNickname || undefined,
              user_mobile: userMobile || undefined,
            }
          : isTeamMembershipList
          ? {
              page,
              page_size: pageSize,
              team_name: teamName || undefined,
              user_nickname: userNickname || undefined,
              user_mobile: userMobile || undefined,
            }
          : isProjectList
            ? {
                page,
                page_size: pageSize,
                project_name: projectName || undefined,
                user_nickname: userNickname || undefined,
                user_mobile: userMobile || undefined,
              }
          : { page, page_size: pageSize, keyword: keyword || undefined },
        signal: controller.signal,
      });
      if (controller.signal.aborted || listAbortRef.current !== controller) return;
      const data = response.data;
      setRows(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (reason) {
      if (controller.signal.aborted || listAbortRef.current !== controller) return;
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      if (isAdminUnauthorizedError(reason)) return;
      const detail = reason instanceof Error ? reason.message : '加载失败';
      setError(detail);
      message.error(detail);
    } finally {
      if (listAbortRef.current === controller) {
        listAbortRef.current = null;
        setLoading(false);
      }
    }
  }, [endpoint, isProjectList, isRechargeList, isTeamMembershipList, isUserAccountList, keyword, merchantOrderId, page, pageSize, paymentEndDate, paymentStartDate, paymentStatus, projectName, teamName, transactionId, userMobile, userNickname]);
  useEffect(() => {
    void load();
    return () => listAbortRef.current?.abort();
  }, [load]);

  const updateQuery = useCallback((changes: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(searchParams);
    let hasChanged = false;
    Object.entries(changes).forEach(([key, value]) => {
      const strValue = value === undefined || value === '' ? null : String(value);
      if (next.get(key) !== strValue) hasChanged = true;
      if (strValue === null) next.delete(key);
      else next.set(key, strValue);
    });
    if (hasChanged) {
      listAbortRef.current?.abort();
      setSearchParams(next, { replace: true });
    } else {
      // 参数完全没变时，强制重新加载（不走 URL 变化路径）
      void load();
    }
  }, [searchParams, setSearchParams, load]);

  useEffect(() => {
    if (!isUserAccountList) return;
    userSearchForm.setFieldsValue({
      user_nickname: userNickname,
      user_mobile: userMobile,
    });
  }, [isUserAccountList, userMobile, userNickname, userSearchForm]);

  useEffect(() => {
    if (!isTeamMembershipList) return;
    teamSearchForm.setFieldsValue({
      team_name: teamName,
      user_nickname: userNickname,
      user_mobile: userMobile,
    });
  }, [isTeamMembershipList, teamName, teamSearchForm, userMobile, userNickname]);

  useEffect(() => {
    if (!isProjectList) return;
    projectSearchForm.setFieldsValue({
      project_name: projectName,
      user_nickname: userNickname,
      user_mobile: userMobile,
    });
  }, [isProjectList, projectName, projectSearchForm, userMobile, userNickname]);

  useEffect(() => {
    if (!isRechargeList) return;
    const hasPaymentRange = Boolean(paymentStartDate && paymentEndDate);
    rechargeSearchForm.setFieldsValue({
      merchant_order_id: merchantOrderId,
      transaction_id: transactionId,
      user_nickname: userNickname,
      user_mobile: userMobile,
      payment_status: paymentStatus,
      payment_time: hasPaymentRange
        ? [dayjs(paymentStartDate), dayjs(paymentEndDate)]
        : null,
    });
  }, [isRechargeList, merchantOrderId, paymentEndDate, paymentStartDate, paymentStatus, rechargeSearchForm, transactionId, userMobile, userNickname]);

  const openDetail = useCallback(async (row: Row) => {
    detailAbortRef.current?.abort();
    setDetail(row);
    setDetailOpen(true);
    const path = detailPath(endpoint, row);
    if (!path) return;
    const controller = new AbortController();
    detailAbortRef.current = controller;
    setDetailLoading(true);
    try {
      const response = await adminApi.get<ApiResponse<Row>>(path, { signal: controller.signal });
      if (controller.signal.aborted || detailAbortRef.current !== controller) return;
      setDetail(response.data);
    } catch (reason) {
      if (controller.signal.aborted || detailAbortRef.current !== controller) return;
      if (reason instanceof ApiError && reason.code === 'REQUEST_ABORTED') return;
      if (isAdminUnauthorizedError(reason)) return;
      message.error(reason instanceof Error ? reason.message : '详情加载失败');
    } finally {
      if (detailAbortRef.current === controller) {
        detailAbortRef.current = null;
        setDetailLoading(false);
      }
    }
  }, [endpoint]);

  const columns = useMemo<TableColumnsType<Row>>(
    () => [...fields.map(field => {
      const rechargeWidths: Record<string, number> = {
        merchant_order_id: 220,
        transaction_id: 220,
        user_nickname: 120,
        user_mobile_masked: 130,
        subject: 160,
        amount_yuan: 110,
        payment_status: 100,
        credited_points: 110,
        reversed_points: 110,
        remaining_reversible_points: 125,
        available_points: 110,
        reversal_status: 120,
        payment_method: 110,
        success_time: 170,
        create_time: 170,
      };
      return {
        title: labelForField(field, definition),
        dataIndex: field,
        key: field,
        width: isRechargeList ? rechargeWidths[field] : undefined,
        fixed: isRechargeList && field === 'merchant_order_id'
          ? 'left' as const
          : undefined,
        ellipsis: true,
        render: (value: unknown, row: Row) => renderValue(value, field, base, row),
      };
    }), {
      title: '操作', key: '__actions', fixed: 'right' as const, width: isRechargeList ? 210 : undefined,
      render: (_value: unknown, row: Row) => <ResourceRowActions endpoint={endpoint} row={row} definition={definition} onReload={load} onDetail={(value: Row) => void openDetail(value)} />,
    }],
    [base, definition, endpoint, fields, isRechargeList, load, openDetail],
  );
  return (
    <AdminPage
      title={title}
      description={description ?? (isRechargeList ? '按支付订单执行积分冲正，请先通过订单号或用户信息定位并核对订单。' : undefined)}
      extra={<ResourcePageExtra endpoint={endpoint} definition={definition} onReload={load} />}
      layout="table"
    >
      {isRechargeList ? (
        <SearchPanel
          form={rechargeSearchForm}
          onSearch={() => {
            const values = rechargeSearchForm.getFieldsValue();
            const paymentRange = values.payment_time;
            updateQuery({
              page: 1,
              keyword: undefined,
              merchant_order_id: values.merchant_order_id?.trim() || undefined,
              transaction_id: values.transaction_id?.trim() || undefined,
              user_nickname: values.user_nickname?.trim() || undefined,
              user_mobile: values.user_mobile?.trim() || undefined,
              payment_status: values.payment_status || '10',
              start_date: paymentRange?.[0]?.format('YYYY-MM-DD'),
              end_date: paymentRange?.[1]?.format('YYYY-MM-DD'),
            });
          }}
          onReset={() => updateQuery({
            page: 1,
            keyword: undefined,
            merchant_order_id: undefined,
            transaction_id: undefined,
            user_nickname: undefined,
            user_mobile: undefined,
            payment_status: '10',
            start_date: undefined,
            end_date: undefined,
          })}
        >
          <Form.Item name="merchant_order_id" label="商户订单号">
            <Input allowClear style={{ width: 190 }} placeholder="输入商户订单号" />
          </Form.Item>
          <Form.Item name="transaction_id" label="渠道流水号">
            <Input allowClear style={{ width: 190 }} placeholder="输入渠道流水号" />
          </Form.Item>
          <Form.Item name="user_nickname" label="用户昵称">
            <Input allowClear style={{ width: 150 }} placeholder="输入用户昵称" />
          </Form.Item>
          <Form.Item name="user_mobile" label="用户手机号">
            <Input allowClear maxLength={20} style={{ width: 160 }} placeholder="输入用户手机号" />
          </Form.Item>
          <Form.Item name="payment_status" label="支付状态">
            <Select
              style={{ width: 130 }}
              options={[
                { value: '10', label: '支付成功' },
                { value: '0', label: '待支付' },
                { value: '20', label: '支付失败' },
                { value: '30', label: '已取消' },
                { value: '40', label: '已退款' },
                { value: 'all', label: '全部状态' },
              ]}
            />
          </Form.Item>
          <Form.Item name="payment_time" label="支付成功时间">
            <AdminDateRangePicker
              style={{ width: 310 }}
              placeholder={['开始日期', '结束日期']}
              startLabel="支付开始日期"
              endLabel="支付结束日期"
            />
          </Form.Item>
        </SearchPanel>
      ) : isUserAccountList ? (
        <SearchPanel
          form={userSearchForm}
          onSearch={() => {
            const values = userSearchForm.getFieldsValue() as Record<string, string | undefined>;
            updateQuery({
              page: 1,
              keyword: undefined,
              user_nickname: values.user_nickname?.trim() || undefined,
              user_mobile: values.user_mobile?.trim() || undefined,
            });
          }}
          onReset={() => updateQuery({
            page: 1,
            keyword: undefined,
            user_nickname: undefined,
            user_mobile: undefined,
          })}
        >
          <Form.Item name="user_nickname" label="用户昵称">
            <Input allowClear placeholder="输入用户昵称" />
          </Form.Item>
          <Form.Item name="user_mobile" label="手机号">
            <Input allowClear maxLength={20} placeholder="输入手机号" />
          </Form.Item>
        </SearchPanel>
      ) : isTeamMembershipList ? (
        <SearchPanel
          form={teamSearchForm}
          onSearch={() => {
            const values = teamSearchForm.getFieldsValue() as Record<string, string | undefined>;
            updateQuery({
              page: 1,
              keyword: undefined,
              team_name: values.team_name?.trim() || undefined,
              user_nickname: values.user_nickname?.trim() || undefined,
              user_mobile: values.user_mobile?.trim() || undefined,
            });
          }}
          onReset={() => updateQuery({
            page: 1,
            keyword: undefined,
            team_name: undefined,
            user_nickname: undefined,
            user_mobile: undefined,
          })}
        >
          <Form.Item name="team_name" label="团队名称">
            <Input allowClear placeholder="输入团队名称" />
          </Form.Item>
          <Form.Item name="user_nickname" label="用户昵称">
            <Input allowClear placeholder="输入用户昵称" />
          </Form.Item>
          <Form.Item name="user_mobile" label="用户手机号">
            <Input allowClear maxLength={20} placeholder="输入用户手机号" />
          </Form.Item>
        </SearchPanel>
      ) : isProjectList ? (
        <SearchPanel
          form={projectSearchForm}
          onSearch={() => {
            const values = projectSearchForm.getFieldsValue() as Record<string, string | undefined>;
            updateQuery({
              page: 1,
              keyword: undefined,
              project_name: values.project_name?.trim() || undefined,
              user_nickname: values.user_nickname?.trim() || undefined,
              user_mobile: values.user_mobile?.trim() || undefined,
            });
          }}
          onReset={() => updateQuery({
            page: 1,
            keyword: undefined,
            project_name: undefined,
            user_nickname: undefined,
            user_mobile: undefined,
          })}
        >
          <Form.Item name="project_name" label="项目名称">
            <Input allowClear placeholder="输入项目名称" />
          </Form.Item>
          <Form.Item name="user_nickname" label="用户昵称">
            <Input allowClear placeholder="输入用户昵称" />
          </Form.Item>
          <Form.Item name="user_mobile" label="用户手机号">
            <Input allowClear maxLength={20} placeholder="输入用户手机号" />
          </Form.Item>
        </SearchPanel>
      ) : (
        <Toolbar>
          <Input.Search
            key={`${endpoint}:${keyword}`}
            allowClear
            defaultValue={keyword}
            placeholder={isRechargeList
              ? '商户订单号 / 渠道流水号 / 用户昵称 / 手机号'
              : '输入关键词搜索'}
            onSearch={value => updateQuery({ page: 1, keyword: value.trim() || undefined })}
          />
        </Toolbar>
      )}
      {error ? <Alert type="error" showIcon title={error} action={<a onClick={() => void load()}>重试</a>} /> : null}
      <DataTable<Row>
        fillHeight
        showRowNumber
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey={row => String(row.id ?? row.user_id ?? row.task_id ?? JSON.stringify(row))}
        pagination={{ current: page, pageSize, total, showSizeChanger: true }}
        onChange={pagination => {
          updateQuery({
            page: pagination.current ?? 1,
            page_size: pagination.pageSize ?? 20,
          });
        }}
      />
      <DetailDrawer open={detailOpen} title={`${title}详情`} width={isPlatformUserList ? 1180 : 760} className={isPlatformUserList ? 'platform-user-detail-drawer' : undefined} onClose={() => {
        detailAbortRef.current?.abort();
        setDetailOpen(false);
      }}>
        {detailLoading ? <Typography.Text type="secondary">详情加载中...</Typography.Text> : null}
        {isPlatformUserList && detail?.id ? (
          <Tabs
            destroyOnHidden
            items={[
              {
                key: 'base',
                label: '基本信息',
                children: (
                  <DetailDescriptions
                    column={2}
                    items={Object.entries(detail)
                      .filter(([key]) => !['username', 'dept_id', 'dept_name'].includes(key))
                      .map(([key, value]) => ({
                        key,
                        label: labelForField(key, definition),
                        children: renderValue(value, key, base, detail),
                      }))}
                  />
                ),
              },
              {
                key: 'personal-credit-consumptions',
                label: '个人积分流水',
                children: <PlatformUserPersonalCreditConsumptions userId={String(detail.id)} />,
              },
              {
                key: 'founded-team-credit-consumptions',
                label: '创始团队积分消耗',
                children: <PlatformUserFoundedTeamCreditConsumptions userId={String(detail.id)} />,
              },
            ]}
          />
        ) : (
          <DetailDescriptions
            column={1}
            items={Object.entries(detail ?? {}).map(([key, value]) => ({
              key,
              label: labelForField(key, definition),
              children: renderValue(value, key, base, detail ?? undefined),
            }))}
          />
        )}
      </DetailDrawer>
    </AdminPage>
  );
}
